// Libraries
import {
  Box,
  FormControl,
  FormLabel,
  Input,
  InputGroup,
  InputRightElement,
  Menu,
  MenuButton,
  MenuList,
} from '@chakra-ui/react';
// Components
import { useDatePicker } from '@hooks/useDatePicker';
import { CalendarIcon } from '@atoms/Icons';
import { CalendarLayout } from '@molecules/index';
// Utils
import text from '@translations/en.json';
// Types
import { DatePickerDropdownProps } from './DatePickerDropdown.types';

/**
 * DatePickerDropdown
 *
 * @component
 * @param {DatePickerDropdownProps} props - The component props.
 * @param {number[]} props.availableYears - The available years in the date picker.
 * @param {number} props.weekStartsOn - The starting day of the week (0 - Sunday, 1 - Monday, etc.).
 * @param {number} props.numberOfMonths - The number of months to display in the date picker.
 * @param {Date} props.minDate - The minimum selectable date in the date picker.
 * @param {Date} props.maxDate - The maximum selectable date in the date picker.
 * @param {Date[]} props.selected - The initially selected dates in the date picker.
 * @param {Function} props.onSelected - The function to handle selected dates.
 * @param {Date} props.defaultDate - The default date for the date picker.
 * @param {string} props.queryParam - The query parameter to use for the date picker's selected date.
 * @param {string} props.label - The label for the date picker.
 * @returns {ReactElement} DatePickerDropdown component.
 */
export const DatePickerDropdown = (props: DatePickerDropdownProps) => {
  // Destructure the props
  const {
    availableYears,
    weekStartsOn,
    numberOfMonths,
    minDate,
    maxDate,
    selected: initialDates,
    onSelected,
    shortcutDate,
    queryParam,
    label,
    ...rest
  } = props;

  const { pickerParams, togglePicker, value, border } = useDatePicker({
    availableYears,
    weekStartsOn,
    numberOfMonths,
    minDate,
    maxDate,
    selected: initialDates,
    onSelected,
    shortcutDate,
    queryParam,
    shortcutDateLabel: '',
  });

  return (
    <Menu>
      {/* eslint-disable-next-line @typescript-eslint/no-explicit-any */}
      <MenuButton as={Box} onClick={togglePicker as any}>
        <FormControl isRequired>
          <FormLabel fontSize='s' mb={1} color='text.faded' fontWeight='normal'>
            {label}
          </FormLabel>
        </FormControl>
        {/* Input group container */}
        {/* eslint-disable-next-line @typescript-eslint/no-explicit-any */}
        <InputGroup onClick={togglePicker as any} cursor='pointer' w={48}>
          {/* Input element */}
          <Input
            h={8}
            border={border}
            bg='white'
            borderRadius='md'
            placeholder={text.select_a_date}
            fontSize='m'
            color='darkGray.100'
            value={value}
            cursor='inherit'
            {...rest}
            readOnly
          />
          {/* Calendar icon */}
          <InputRightElement h={8}>
            <CalendarIcon color='tealishBlue' />
          </InputRightElement>
        </InputGroup>
      </MenuButton>
      <MenuList>
        <CalendarLayout {...pickerParams} />
      </MenuList>
    </Menu>
  );
};
